import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  PlusCircle,
  Search,
  Upload,
  Edit,
  Trash2,
  CheckCircle2,
  X,
  Loader2,
  AlertCircle,
  Calendar,
  MessageSquare,
  Ban,
} from 'lucide-react';
import {
  Card,
  CardContent,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { useToast } from '../../../components/ui/toaster.jsx';
import {
  fetchClientDocuments,
  bulkCreateClientDocuments,
  deleteClientDocument,
  uploadDocumentToR2,
  getDocumentDownloadUrl,
} from '../Services/clientDocumentsService';
import { listAllDocumentTypes } from '../../parametrizacion/document-types/Services/documentTypesService';
import { cn } from '../../../lib/utils';

const MAX_FILE_SIZE_MB = 25;
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv';

const ESTADO_BADGE = {
  Pendiente: 'warning',
  Cargado: 'success',
  Vencido: 'destructive',
  Rechazado: 'secondary',
};

const formatDate = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return '—';
  }
};

export default function DocumentsSection({ clienteId, clienteNombre }) {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLabel, setDeleteLabel] = useState('');
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const pendingTargetRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchClientDocuments(clienteId);
      if (!r?.success) {
        toast.error('Error al cargar documentos', r?.message);
        return;
      }
      setData(r.data || []);
    } catch (e) {
      toast.error('Error al cargar documentos', e?.message);
    } finally {
      setLoading(false);
    }
  }, [clienteId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const askDelete = (d) => {
    setDeleteId(d._id);
    setDeleteLabel(d.nombre || d.tipoNombre);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const r = await deleteClientDocument(clienteId, deleteId);
      if (r?.success) {
        toast.success('Documento eliminado', deleteLabel);
      } else {
        toast.error('Error al eliminar', r?.message);
        return;
      }
    } catch (e) {
      toast.error('Error inesperado', e?.message);
    } finally {
      setDeleteId(null);
      setDeleteLabel('');
      load();
    }
  };

  // ---------- Upload a R2 ----------

  const triggerUpload = (doc) => {
    pendingTargetRef.current = doc;
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    // Limpia el input para permitir re-seleccionar el mismo archivo
    e.target.value = '';
    if (!file) return;
    const doc = pendingTargetRef.current;
    pendingTargetRef.current = null;
    if (!doc) return;

    // Validación cliente
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(
        'Archivo demasiado grande',
        `Máximo permitido: ${MAX_FILE_SIZE_MB} MB`,
      );
      return;
    }
    if (!file.type) {
      toast.error('Tipo de archivo no detectado', 'Intenta con otro archivo');
      return;
    }

    setUploadingId(doc._id);
    setUploadProgress(0);
    try {
      const r = await uploadDocumentToR2(clienteId, doc._id, file, {
        onProgress: setUploadProgress,
      });
      if (!r?.success) {
        toast.error('No se pudo subir el archivo', r?.message);
        return;
      }
      toast.success(
        'Archivo subido',
        `${file.name} · ${(file.size / 1024).toFixed(1)} KB`,
      );
      load();
    } catch (e2) {
      toast.error('Error al subir', e2?.message || 'Intenta nuevamente');
    } finally {
      setUploadingId(null);
      setUploadProgress(0);
    }
  };

  const onDownload = async (doc) => {
    try {
      const r = await getDocumentDownloadUrl(clienteId, doc._id);
      if (!r?.success) {
        toast.error('No se pudo obtener el enlace', r?.message);
        return;
      }
      window.open(r.data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error('Error al generar enlace', e?.message);
    }
  };

  const total = data.length;
  const totalPendientes = data.filter((d) => d.estado === 'Pendiente').length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="space-y-4"
    >
      <Card>
        <CardContent className="p-4 md:p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 p-2.5 rounded-lg shrink-0">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  {total === 0
                    ? '0 documentos requeridos'
                    : `${total} documento${total === 1 ? '' : 's'} requerido${
                        total === 1 ? '' : 's'
                      }`}
                </p>
                {total > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {totalPendientes} pendiente{totalPendientes === 1 ? '' : 's'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => setModalOpen(true)}>
                <PlusCircle className="h-4 w-4" />
                Agregar documentos
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <DocumentsTable
        data={data}
        loading={loading}
        onDelete={askDelete}
        onUpload={triggerUpload}
        onDownload={onDownload}
        uploadingId={uploadingId}
        uploadProgress={uploadProgress}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        onChange={onFileSelected}
        className="hidden"
        aria-hidden="true"
      />

      <SelectDocumentsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        clienteId={clienteId}
        clienteNombre={clienteNombre}
        alreadyAddedIds={new Set(data.map((d) => String(d.documentTypeId)))}
        onSaved={() => {
          setModalOpen(false);
          load();
        }}
      />

      <AlertDialog
        open={Boolean(deleteId)}
        onOpenChange={(o) => !o && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el documento requerido <strong>{deleteLabel}</strong>. Esta
              acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}

// ---------- Tabla de documentos ----------

function DocumentsTable({ data, loading, onDelete, onUpload, onDownload, uploadingId, uploadProgress }) {
  if (loading && data.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          <Loader2 className="inline h-4 w-4 animate-spin mr-2" />
          Cargando documentos…
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
          <p className="font-medium text-foreground">Aún no hay documentos requeridos</p>
          <p className="text-sm mt-1">
            Usa <strong>Agregar documentos</strong> para seleccionar los tipos de documento
            que el cliente debe aportar.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="px-4 py-3 text-left font-semibold uppercase text-[11px] tracking-wide">
                  Tipo
                </th>
                <th className="px-4 py-3 text-left font-semibold uppercase text-[11px] tracking-wide">
                  Nombre / Referencia
                </th>
                <th className="px-4 py-3 text-left font-semibold uppercase text-[11px] tracking-wide">
                  Vencimiento
                </th>
                <th className="px-4 py-3 text-left font-semibold uppercase text-[11px] tracking-wide">
                  Estado
                </th>
                <th className="px-4 py-3 text-left font-semibold uppercase text-[11px] tracking-wide">
                  Archivo subido
                </th>
                <th className="px-4 py-3 text-center font-semibold uppercase text-[11px] tracking-wide w-[200px]">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((d) => {
                const isUploading = uploadingId === d._id;
                return (
                  <tr
                    key={d._id}
                    className="border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors"
                  >
                    <td className="px-4 py-3 align-top">
                      <Badge variant="outline" className="font-normal bg-blue-50 text-blue-700 border-blue-100">
                        {d.tipoNombre}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 align-top font-medium text-foreground">
                      {d.nombre}
                    </td>
                    <td className="px-4 py-3 align-top text-muted-foreground">
                      {d.requiereVencimiento ? (
                        formatDate(d.fechaVencimiento)
                      ) : (
                        <span className="text-muted-foreground/50">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <Badge variant={ESTADO_BADGE[d.estado] || 'secondary'}>
                        {d.estado}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 align-top">
                      {d.archivo ? (
                        <button
                          type="button"
                          onClick={() => onDownload(d)}
                          className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline max-w-[200px] truncate"
                          title={d.archivo.nombre}
                        >
                          <FileText className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{d.archivo.nombre}</span>
                        </button>
                      ) : isUploading ? (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-[160px]">
                          <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                          <div className="flex-1 h-1.5 rounded bg-muted overflow-hidden">
                            <div
                              className="h-full bg-primary transition-all"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                          <span className="shrink-0 tabular-nums">{uploadProgress}%</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground italic text-xs">Sin archivo</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center justify-center gap-1">
                        <ActionButton
                          icon={isUploading ? Loader2 : Upload}
                          tooltip={isUploading ? `Subiendo… ${uploadProgress}%` : 'Subir archivo'}
                          onClick={() => !isUploading && onUpload(d)}
                          disabled={isUploading}
                          spin={isUploading}
                        />
                        <ActionButton
                          icon={Edit}
                          tooltip="Editar (próximamente)"
                          onClick={() => {}}
                          disabled
                        />
                        <ActionButton
                          icon={Ban}
                          tooltip="Marcar como rechazado (próximamente)"
                          onClick={() => {}}
                          disabled
                        />
                        <ActionButton
                          icon={MessageSquare}
                          tooltip="Comentario (próximamente)"
                          onClick={() => {}}
                          disabled
                        />
                        <ActionButton
                          icon={Trash2}
                          tooltip="Eliminar"
                          onClick={() => onDelete(d)}
                          className="hover:bg-destructive hover:text-destructive-foreground"
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function ActionButton({ icon: Icon, tooltip, onClick, disabled, className, spin = false }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClick}
      title={tooltip}
      aria-label={tooltip}
      disabled={disabled}
      className={cn('h-8 w-8 text-muted-foreground', className)}
    >
      <Icon className={cn('h-4 w-4', spin && 'animate-spin')} />
    </Button>
  );
}

// ---------- Modal: seleccionar documentos requeridos ----------

function SelectDocumentsModal({
  isOpen,
  onClose,
  clienteId,
  clienteNombre,
  alreadyAddedIds,
  onSaved,
}) {
  const toast = useToast();
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({}); // { typeId: { tipo, fechaVencimiento? } }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setSelected({});
    setLoadingTypes(true);
    listAllDocumentTypes()
      .then((r) => {
        if (r?.success) {
          setTypes(r.data || []);
        } else {
          toast.error('No se pudieron cargar los tipos de documento', r?.message);
        }
      })
      .catch((e) => toast.error('Error al cargar tipos', e?.message))
      .finally(() => setLoadingTypes(false));
  }, [isOpen, toast]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return types
      .filter((t) => t.estado === 'Activo')
      .filter((t) => !alreadyAddedIds.has(String(t._id)))
      .filter((t) => !q || t.nombre.toLowerCase().includes(q));
  }, [types, search, alreadyAddedIds]);

  const toggle = (tipo) => {
    setSelected((prev) => {
      const id = String(tipo._id);
      if (prev[id]) {
        const n = { ...prev };
        delete n[id];
        return n;
      }
      return { ...prev, [id]: { tipo, fechaVencimiento: '' } };
    });
  };

  const setFecha = (id, value) => {
    setSelected((prev) => ({
      ...prev,
      [id]: { ...prev[id], fechaVencimiento: value },
    }));
  };

  const selectedCount = Object.keys(selected).length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedCount === 0) return;

    // Validar fechas requeridas
    const missing = [];
    Object.values(selected).forEach(({ tipo, fechaVencimiento }) => {
      if (tipo.requiereVencimiento && !fechaVencimiento) {
        missing.push(tipo.nombre);
      }
    });
    if (missing.length) {
      toast.error(
        'Falta fecha de vencimiento',
        `Estos tipos requieren vencimiento: ${missing.join(', ')}`,
      );
      return;
    }

    setSaving(true);
    try {
      const items = Object.values(selected).map(({ tipo, fechaVencimiento }) => ({
        documentTypeId: String(tipo._id),
        nombre: tipo.nombre,
        fechaVencimiento: fechaVencimiento
          ? new Date(fechaVencimiento).toISOString()
          : undefined,
      }));
      const r = await bulkCreateClientDocuments(clienteId, items);
      if (!r?.success) {
        toast.error('No se pudieron guardar los documentos', r?.message);
        return;
      }
      const { created, skipped } = r.data || {};
      if (created?.length) {
        toast.success(
          `${created.length} documento(s) agregado(s)`,
          skipped?.length ? `${skipped.length} omitido(s) (ya existían)` : undefined,
        );
      } else {
        toast.info('No se agregaron documentos nuevos', 'Todos ya estaban agregados');
      }
      onSaved();
    } catch (e) {
      toast.error('Error inesperado', e?.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Seleccionar documentos requeridos"
      subtitle={clienteNombre ? `Documentos que ${clienteNombre} debe aportar.` : undefined}
      maxWidth="max-w-2xl"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            {selectedCount === 0
              ? 'Sin selección'
              : `${selectedCount} seleccionado${selectedCount === 1 ? '' : 's'}`}
          </p>
        </div>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar tipo de documento…"
            className="pl-9"
            autoFocus
          />
        </div>

        <div className="border border-dashed border-border rounded-md max-h-[420px] overflow-y-auto">
          {loadingTypes ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <Loader2 className="inline h-4 w-4 animate-spin mr-2" />
              Cargando tipos de documento…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {search
                ? 'Sin resultados para tu búsqueda.'
                : alreadyAddedIds.size > 0
                  ? 'Ya agregaste todos los tipos de documento disponibles para este cliente.'
                  : 'No hay tipos de documento activos. Crea primero el catálogo en Parametrizaciones → Tipos de Documento.'}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((tipo) => {
                const id = String(tipo._id);
                const isSelected = Boolean(selected[id]);
                return (
                  <li
                    key={id}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 transition-colors',
                      isSelected ? 'bg-primary/5' : 'hover:bg-muted/40',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(tipo)}
                      className="h-4 w-4 rounded border-input text-primary focus:ring-primary shrink-0"
                      aria-label={`Seleccionar ${tipo.nombre}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'text-sm font-medium truncate',
                            isSelected ? 'text-primary' : 'text-foreground',
                          )}
                        >
                          {tipo.nombre}
                        </span>
                        {tipo.requiereVencimiento && (
                          <Badge variant="info" className="font-normal text-[10px] py-0 px-1.5 shrink-0">
                            <Calendar className="h-3 w-3 mr-1" /> Vence
                          </Badge>
                        )}
                      </div>
                      {tipo.descripcion && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {tipo.descripcion}
                        </p>
                      )}
                    </div>
                    {isSelected && tipo.requiereVencimiento && (
                      <div className="flex items-center gap-2 shrink-0">
                        <Label className="text-xs text-muted-foreground whitespace-nowrap">
                          Vence:
                        </Label>
                        <Input
                          type="date"
                          value={selected[id]?.fechaVencimiento || ''}
                          onChange={(e) => setFecha(id, e.target.value)}
                          className="h-9 w-[160px]"
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={selectedCount === 0 || saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PlusCircle className="h-4 w-4" />
            )}
            {saving
              ? 'Agregando…'
              : `Agregar ${selectedCount} documento${selectedCount === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

// ---------- Modal genérico (mismo shell que ContactsSection) ----------

function ModalShell({ isOpen, onClose, title, subtitle, maxWidth = 'max-w-lg', children }) {
  if (!isOpen) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        className={cn(
          'bg-card p-6 rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto border border-border',
          maxWidth,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start gap-4 mb-4">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold text-foreground">{title}</h2>
            {subtitle && (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Cerrar"
            className="-mt-1 -mr-1"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
}
